import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
// import { getCityFromHost } from '../utils/getCityFromHost';
import { isAdminLoggedIn, logoutAdmin, isUserLoggedIn, logoutUser, getUserInfo, isImpersonating, stopImpersonation } from '../utils/auth';
import { useSubmitBusiness } from '../contexts/SubmitBusinessContext';
// import DarkModeToggle from './DarkModeToggle';
import { Search, Home, FileText, LogIn, User, LogOut, Shield, ArrowLeft, X, Heart, MapPin, LayoutGrid, Menu, ChevronRight, Droplet, Bus, BookOpen } from 'lucide-react';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const adminLoggedIn = isAdminLoggedIn();
  const userLoggedIn = isUserLoggedIn();
  const userInfo = getUserInfo();
  const impersonating = isImpersonating();
  const { openModal } = useSubmitBusiness();
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const searchRef = useRef(null);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleAdminLogout = () => {
    logoutAdmin();
    navigate('/');
  };

  const handleUserLogout = () => {
    if (impersonating) {
      // If impersonating, return to admin instead of logging out
      handleStopImpersonation();
    } else {
      logoutUser();
      navigate('/');
    }
  };

  const handleStopImpersonation = () => {
    stopImpersonation();
    navigate('/admin/dashboard');
    window.location.reload();
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (userInfo?.name) {
      const names = userInfo.name.trim().split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return userInfo.name[0].toUpperCase();
    }
    return 'U';
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
      setSearchTerm('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(e);
    }
  };

  // Show public navbar on all routes (including admin routes)
  return (
    <nav className="bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-4 lg:space-x-8">
            <Link to="/" className="flex items-center space-x-3 group shrink-0">
              <div className="w-14 h-14 rounded-md flex items-center justify-center overflow-hidden">
                <img 
                  src="/digi_fatehabad_logo.png" 
                  alt="Digital Fatehabad Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="hidden sm:block">
                <div className="text-base font-semibold text-gray-900 dark:text-gray-50">
                  Digital Fatehabad
                </div>
              </div>
            </Link>

            {/* Desktop Navigation Links */}
            {!isAdminRoute && (
              <div className="hidden lg:flex items-center space-x-1">
                <Link
                  to="/"
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors rounded-md hover:bg-gray-50 dark:hover:bg-gray-900"
                >
                  <Home className="w-4 h-4" />
                  Home
                </Link>
                <Link
                  to="/categories"
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors rounded-md hover:bg-gray-50 dark:hover:bg-gray-900"
                >
                  <LayoutGrid className="w-4 h-4 text-primary-500" />
                  Categories
                </Link>
                <Link
                  to="/villages"
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors rounded-md hover:bg-gray-50 dark:hover:bg-gray-900"
                >
                  <MapPin className="w-4 h-4 text-primary-500" />
                  Villages
                </Link>
                <Link
                  to="/blog"
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors rounded-md hover:bg-gray-50 dark:hover:bg-gray-900"
                >
                  <BookOpen className="w-4 h-4 text-blue-500" />
                  Blog
                </Link>

                <button
                  onClick={openModal}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors rounded-md hover:bg-gray-50 dark:hover:bg-gray-900"
                >
                  <FileText className="w-4 h-4" />
                  Submit Business
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            {/* Search Bar - Positioned before Profile / Auth icon */}
            {!isAdminRoute && (
              <>
                {/* Desktop Search Bar */}
                <form onSubmit={handleSearch} className="hidden md:block w-48 lg:w-60 xl:w-72">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Search businesses, services..."
                      className="w-full px-3.5 py-1.5 pl-9 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 placeholder-gray-500 dark:placeholder-gray-400"
                      ref={searchRef}
                    />
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm('')}
                        className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </form>

                {/* Mobile Search Button */}
                <button
                  onClick={() => navigate('/search')}
                  className="md:hidden p-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900"
                  title="Search"
                >
                  <Search className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Profile Avatar / User Menu */}
            {userLoggedIn && !isAdminRoute && (
              <div className="flex items-center gap-2 md:gap-3">
                {impersonating && (
                  <button
                    onClick={handleStopImpersonation}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-warning-600 text-white hover:bg-warning-700 transition-colors rounded-lg font-semibold"
                    title="Return to Admin Panel"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="hidden md:inline">Return to Admin</span>
                    <span className="md:hidden">Admin</span>
                  </button>
                )}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    title={userInfo?.name || 'User'}
                  >
                    {getUserInitials()}
                  </button>
                  
                  {/* User Dropdown Menu */}
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50 animate-scale-in">
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate">
                          {userInfo?.name || 'User'}
                        </p>
                        {userInfo?.email && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                            {userInfo.email}
                          </p>
                        )}
                      </div>
                      <Link
                        to="/profile"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <User className="w-5 h-5" />
                        View Profile
                      </Link>
                      <Link
                        to="/profile#favourites"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Heart className="w-5 h-5 text-red-500" />
                        My Favourites
                      </Link>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          handleUserLogout();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <LogOut className="w-5 h-5" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!userLoggedIn && !isAdminRoute && !adminLoggedIn && (
              <Link
                to="/login"
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors rounded-md hover:bg-gray-50 dark:hover:bg-gray-900"
                title="Login"
              >
                <LogIn className="w-5 h-5" />
              </Link>
            )}

            {adminLoggedIn && !isAdminRoute && (
              <div className="flex items-center gap-2">
                <Link
                  to="/admin/dashboard"
                  className="p-2 bg-gray-900 dark:bg-gray-800 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-700 transition-colors"
                  title="Admin Panel"
                  aria-label="Admin Panel"
                >
                  <Shield className="w-5 h-5" />
                </Link>
                <button
                  onClick={handleAdminLogout}
                  className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  title="Logout"
                  aria-label="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Mobile Hamburger Menu Toggle Button */}
            {!isAdminRoute && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900"
                aria-label="Toggle Navigation Menu"
                title="Menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Horizontal Topbar Menu Strip (Sticky right below main header) */}
      {!isAdminRoute && (
        <div className="lg:hidden border-t border-gray-100 dark:border-gray-800/80 bg-white/95 dark:bg-gray-950/95 backdrop-blur-md px-3 py-2 flex items-center gap-1.5 overflow-x-auto no-scrollbar scroll-smooth">
          <Link
            to="/"
            className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap active:scale-95 transition-all ${
              location.pathname === '/'
                ? 'bg-primary-600 text-white font-semibold shadow-xs'
                : 'bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border border-gray-200/80 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <Home className="w-3.5 h-3.5" />
            <span>Home</span>
          </Link>

          <Link
            to="/categories"
            className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap active:scale-95 transition-all ${
              location.pathname.startsWith('/categories') || location.pathname.startsWith('/category')
                ? 'bg-primary-600 text-white font-semibold shadow-xs'
                : 'bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border border-gray-200/80 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5 text-primary-500" />
            <span>Categories</span>
          </Link>

          <Link
            to="/villages"
            className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap active:scale-95 transition-all ${
              location.pathname.startsWith('/village')
                ? 'bg-primary-600 text-white font-semibold shadow-xs'
                : 'bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 border border-gray-200/80 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
          >
            <MapPin className="w-3.5 h-3.5 text-primary-500" />
            <span>Villages</span>
          </Link>

          <button
            onClick={openModal}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-800 whitespace-nowrap active:scale-95 transition-all shadow-2xs"
          >
            <FileText className="w-3.5 h-3.5" />
            <span>+ Submit</span>
          </button>
        </div>
      )}

      {/* Mobile Slide-Down Menu Drawer (When Hamburger is clicked) */}
      {!isAdminRoute && mobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-2xl max-h-[calc(100vh-4rem)] overflow-y-auto animate-fade-in divide-y divide-gray-100 dark:divide-gray-800/80">
          {/* Main Navigation Links */}
          <div className="p-3 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                location.pathname === '/'
                  ? 'bg-primary-50 dark:bg-primary-950/60 text-primary-600 dark:text-primary-400'
                  : 'text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900'
              }`}
            >
              <div className="flex items-center gap-3">
                <Home className="w-5 h-5 text-primary-500" />
                <span>Home</span>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </Link>

            <Link
              to="/categories"
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                location.pathname.startsWith('/categories')
                  ? 'bg-primary-50 dark:bg-primary-950/60 text-primary-600 dark:text-primary-400'
                  : 'text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900'
              }`}
            >
              <div className="flex items-center gap-3">
                <LayoutGrid className="w-5 h-5 text-primary-500" />
                <span>Categories</span>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </Link>

            <Link
              to="/villages"
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                location.pathname.startsWith('/village')
                  ? 'bg-primary-50 dark:bg-primary-950/60 text-primary-600 dark:text-primary-400'
                  : 'text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900'
              }`}
            >
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-emerald-500" />
                <span>Villages (270+ गांव)</span>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </Link>

            <Link
              to="/blog"
              onClick={() => setMobileMenuOpen(false)}
              className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                location.pathname.startsWith('/blog')
                  ? 'bg-primary-50 dark:bg-primary-950/60 text-primary-600 dark:text-primary-400'
                  : 'text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900'
              }`}
            >
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-blue-500" />
                <span>Blog Articles</span>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </Link>

          </div>

          {/* Quick Utility Services */}
          <div className="p-3 bg-gray-50/70 dark:bg-gray-900/40">
            <p className="px-3 text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
              Citizen Services
            </p>
            <div className="grid grid-cols-3 gap-2">
              <Link
                to="/blood-donors"
                onClick={() => setMobileMenuOpen(false)}
                className="flex flex-col items-center justify-center p-2.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-center hover:border-red-300 transition-colors"
              >
                <Droplet className="w-5 h-5 text-red-500 mb-1" />
                <span className="text-[11px] font-semibold text-gray-800 dark:text-gray-200">Blood Donors</span>
              </Link>

              <Link
                to="/bus-time-table"
                onClick={() => setMobileMenuOpen(false)}
                className="flex flex-col items-center justify-center p-2.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-center hover:border-blue-300 transition-colors"
              >
                <Bus className="w-5 h-5 text-blue-500 mb-1" />
                <span className="text-[11px] font-semibold text-gray-800 dark:text-gray-200">Bus Timetable</span>
              </Link>

              <Link
                to="/officers"
                onClick={() => setMobileMenuOpen(false)}
                className="flex flex-col items-center justify-center p-2.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-center hover:border-emerald-300 transition-colors"
              >
                <Shield className="w-5 h-5 text-emerald-500 mb-1" />
                <span className="text-[11px] font-semibold text-gray-800 dark:text-gray-200">Officers</span>
              </Link>
            </div>
          </div>

          {/* Submit Business Action */}
          <div className="p-4 bg-white dark:bg-gray-950">
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                openModal();
              }}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold text-sm bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white shadow-md active:scale-98 transition-all"
            >
              <FileText className="w-4 h-4" />
              <span>Submit Free Business Listing</span>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;

